/**
 * Speech Therapy Visit Note (Follow-Up) — options + empty payload.
 * Common header (1–6) via StNotesCommonHeader; clinical sections 7–17 per mockups.
 */

import { emptyStCommonHeader } from "./stNotesCommonHeaderUtils";
import { newGoalId } from "../ptNotes/ptGoals";

export const ARTICULATION_OPTIONS = ["WFL", "Mild", "Mod"];
export const VOICE_OPTIONS = ["WFL", "Hoarse", "Breathy"];
export const FLUENCY_OPTIONS = ["WFL", "Stuttering"];

export const EXPRESSIVE_STATUS_OPTIONS = [
  "WFL",
  "Mild Impairment",
  "Moderate",
];

export const RECEPTIVE_STATUS_OPTIONS = ["WFL", "Mild", "Moderate"];

export const COGNITIVE_ASSESSMENT_OPTIONS = [
  "Orientation (Person, Place, Time)",
  "Sustained Attention",
  "Short-term Memory Deficits",
  "Problem Solving/Safety Awareness",
];

export const DIET_CONSISTENCY_OPTIONS = [
  "Regular",
  "Soft/Bite-Sized",
  "Minced & Moist",
  "Pureed",
  "NPO",
];

export const LIQUID_CONSISTENCY_OPTIONS = [
  "Thin",
  "Slightly Thick",
  "Moderately Thick",
  "Extremely Thick",
  "NPO",
];

export const ASPIRATION_SIGN_OPTIONS = ["Cough", "None"];

export const INTERVENTION_OPTIONS = [
  "Oral Motor Exercises",
  "Compensatory Strategies",
  "Cognitive Retraining",
  "Aphasia Management",
];

export const EDUCATION_TOPIC_OPTIONS = [
  "Safe Swallowing Precautions",
  "Home Exercise Review",
];

export const GOAL_STATUS_OPTIONS = [
  "Met",
  "In Progress",
  "Partially Met",
  "Not Met",
];

export const ST_VISIT_ARRAY_KEYS = [
  "cognitiveAssessment",
  "aspirationSigns",
  "interventions",
  "educationTopics",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const parsePainLevel = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 10 ? n : null;
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyProgressGoalRow = (n = 1) => ({
  id: newGoalId(`pg-${n}`),
  goal: "",
  status: "",
  accPercent: "",
});

export const emptyProgressGoals = () => [
  emptyProgressGoalRow(1),
  emptyProgressGoalRow(2),
];

export const normalizeProgressGoals = (rows, minCount = 2) => {
  const goals = Array.isArray(rows) ? rows : [];
  if (!goals.length) return emptyProgressGoals();
  const normalized = goals.map((row, idx) => ({
    id: row?.id || newGoalId(`pg-${idx + 1}`),
    goal: String(row?.goal || row?.description || "").trim(),
    status: String(row?.status || "").trim(),
    accPercent: String(row?.accPercent || row?.acc || "").trim(),
  }));
  while (normalized.length < minCount) {
    normalized.push(emptyProgressGoalRow(normalized.length + 1));
  }
  return normalized;
};

export const emptyStVisitPayload = () => ({
  billingCode: "G0153",
  timeIn: "",
  timeOut: "",
  visitDate: "",
  commonHeader: emptyStCommonHeader(),

  // —— 7. Subjective Findings ——
  patientCaregiverReport: "",
  painLevel: null,

  // —— 8. Speech Assessment ——
  articulation: "",
  voice: "",
  fluency: "",

  // —— 9. Language Assessment ——
  expressiveStatus: "",
  receptiveStatus: "",

  // —— 10. Cognitive Assessment ——
  cognitiveAssessment: [],

  // —— 11. Swallowing/Dysphagia ——
  dietConsistency: "",
  liquidConsistency: "",
  aspirationSigns: [],

  // —— 12. Interventions Provided ——
  interventions: [],

  // —— 13. Patient/Caregiver Education ——
  educationTopics: [],
  educationDetails: "",

  // —— 14. Progress Toward Goals ——
  progressGoals: emptyProgressGoals(),

  // —— 15. Home Exercise/Program ——
  homeExerciseRecommendations: "",

  // —— 16. Plan for Next Visit ——
  visitFrequency: "",
  nextScheduledDate: "",

  // —— 17. Additional Notes ——
  additionalNotes: "",
});
